/**
 *
 * Trip Page
 *
 */

import { DndContext, DragOverlay } from '@dnd-kit/core';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Tab } from '@mui/material';
import Box from '@mui/material/Box';
import { SerializedError } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';
import React, { SyntheticEvent, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Descendant } from 'slate';
import useAccessControl from '../../hooks/useAccessControl';
import useDebouncedCallback from '../../hooks/useDebouncedCallback';
import useMobileMediaQuery from '../../hooks/useMobileMediaQuery';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  selectCards,
  selectCurrentTrip,
  selectError,
  selectMapCenter,
  selectMapZoom,
  selectTripLocation,
} from '../../redux/trip/trip.selectors';
import {
  clearCurrentTrip,
  fetchTrip,
  updateMapCenter,
  updateMapZoom,
  updateTrip,
} from '../../redux/trip/trip.slice';
import { canEditTrip } from '../../redux/trip/trip.utils';
import { selectCurrentUser, selectIsAuthInitialized } from '../../redux/user/user.selectors';
import { Marker } from '../../types/MapMarker';
import { isNotesEmpty } from '../../utils/slate.utils';
import SingleCard from '../Card/SingleCard';
import CardDetailsModal from '../CardDetailsModal/CardDetailsModal';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import Spinner from '../Spinner/Spinner';
import DragOverlayContent from './DnD/DragOverlayContent';
import { DROP_ANIMATION } from './DnD/types';
import { useTripDndHandlers } from './DnD/useTripDndHandlers';
import Itinerary from './Itinerary/Itinerary';
import TripMap from './TripMap/TripMap';
import TripPlayground from './TripPlayground/TripPlayground';
import TripShare from './TripShare/TripShare';
import TripTitleHeader from './TripTitleHeader/TripTitleHeader';
import './Trip.scss';

function Trip() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobile = useMobileMediaQuery();
  const { tripId } = useParams();
  const tripError = useAppSelector(selectError);
  const currentUser = useAppSelector(selectCurrentUser);
  const currentTrip = useAppSelector(selectCurrentTrip);
  const isAuthInitialized = useAppSelector(selectIsAuthInitialized);
  const cards = useAppSelector(selectCards);
  const location = useAppSelector(selectTripLocation);
  const mapCenter = useAppSelector(selectMapCenter);
  const mapZoom = useAppSelector(selectMapZoom);
  // ─── Drag and Drop ───────────────────────────────────────────────────────
  const {
    sensors,
    collisionDetectionStrategy,
    activeItem,
    pendingDrop,
    handleDragStart,
    handleDragEnd,
    handleConfirmDrop,
    handleCancelDrop,
  } = useTripDndHandlers(tripId);

  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || 'playground';
  const cardDetailToDisplayId = searchParams.get('card') || null;

  const canEdit = Boolean(currentTrip && canEditTrip(currentTrip, currentUser));
  const notes = currentTrip ? currentTrip.notes : null;
  const isLoading = !isAuthInitialized || !currentTrip || (tripId && currentTrip?.id !== tripId);

  // Monitor trip access permissions and redirect if access is revoked
  // DYNAMIC ACCESS CONTROL: Monitor trip permissions in real-time
  // This hook watches for changes to trip.public or user roles and automatically
  // redirects users to /forbidden if they lose access while viewing the trip.
  // Handles cases like: owner makes trip private, user role revoked, etc.
  useAccessControl(currentTrip, currentUser, tripError);

  const debouncedSaveNotes = useDebouncedCallback((newNotes: Descendant[]) => {
    if (currentTrip && tripId && !isEqual(newNotes, currentTrip.notes)) {
      dispatch(
        updateTrip({
          tripId,
          field: 'notes',
          value: newNotes,
        })
      );
    }
  }, 500);

  // biome-ignore lint/correctness/useExhaustiveDependencies: needed
  useEffect(() => {
    if (tripId && isAuthInitialized) {
      if (currentTrip && currentTrip?.id !== tripId) {
        dispatch(clearCurrentTrip());
      }

      const STALE_THRESHOLD_MS = 5 * 60 * 1000;
      const isSameTrip = currentTrip?.id === tripId;
      const lastFetched = currentTrip?.lastFetchedAt
        ? new Date(currentTrip.lastFetchedAt).getTime()
        : 0;
      // set to true if the cache age is within the allowed stale threshold (under 5 min), meaning we can skip a refetch
      const isWithinThreshold = isSameTrip && Date.now() - lastFetched < STALE_THRESHOLD_MS;
      // listOrder holds IDs for both lists and dividers (collapsible section headers).
      // If any ID in listOrder has no matching list, it belongs to a divider. If dividers
      // is empty despite that, dividers failed to load and were cached in a broken state —
      // force a refetch instead of showing the user incomplete data.
      const hasMissingDividers =
        isWithinThreshold &&
        currentTrip?.listOrder?.some((id) => !currentTrip.lists.find((l) => l.id === id)) ===
          true &&
        !currentTrip?.dividers?.length;
      const isFresh = isWithinThreshold && !hasMissingDividers;

      if (!isFresh) {
        sessionStorage.removeItem('selectedLists');
        dispatch(fetchTrip({ tripID: tripId, userID: currentUser?.uid }));
      }
    } else if (!tripId) {
      navigate('/');
    }
  }, [dispatch, navigate, tripId, currentUser?.uid, isAuthInitialized]);

  // Route to appropriate error pages based on error type
  useEffect(() => {
    if (tripError && tripId) {
      const errorCode = (tripError as SerializedError)?.code;

      if (errorCode === 'permission-denied') {
        navigate('/forbidden');
      } else if (errorCode === 'trip-not-found') {
        navigate('/404');
      }
    }
  }, [navigate, tripError, tripId]);

  // Clean up trip data when component unmounts
  useEffect(
    () => () => {
      dispatch(clearCurrentTrip());
    },
    [dispatch]
  );

  const handleTabChange = useCallback(
    (event: SyntheticEvent, newValue: string) => {
      searchParams.set('tab', newValue);
      searchParams.delete('list');
      searchParams.delete('card');
      searchParams.delete('card-tab');
      searchParams.delete('photos');
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleMarkerClick = useCallback(
    (marker: Marker) => {
      searchParams.set('card', marker.id);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleMapCenterChange = useDebouncedCallback((lat: number, lng: number) => {
    if (currentTrip) {
      dispatch(updateMapCenter({ lat, lng }));
    }
  }, 1000);

  const handleMapZoomChange = useDebouncedCallback((zoom: number) => {
    if (currentTrip) {
      dispatch(updateMapZoom(zoom));
    }
  }, 1000);

  const handleCardDetailsClose = useCallback(() => {
    searchParams.delete('card');
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  return isLoading || !currentTrip ? (
    <Spinner />
  ) : (
    <>
      <Helmet>
        <title>
          Wander | {currentTrip?.title || 'Loading...'} |{' '}
          {currentTab.charAt(0).toUpperCase() + currentTab.slice(1)}
        </title>
      </Helmet>
      <div className='trip-container'>
        <div className='trip-header-container'>
          <TripTitleHeader
            trip={currentTrip}
            canEdit={canEdit}
            currentUser={currentUser}
          />
          {!isMobile && (
            <TripShare
              trip={currentTrip}
              canEdit={canEdit}
            />
          )}
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetectionStrategy}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Box
            className='trip-main-container'
            data-hide-fade={currentTab === 'map'}
          >
            <TabContext value={currentTab}>
              <TabList
                className='trip-tab-list'
                onChange={handleTabChange}
              >
                <Tab
                  label='Playground'
                  value='playground'
                />
                <Tab
                  label='Map'
                  value='map'
                />
                {(canEdit || Object.keys(currentTrip?.events || {}).length > 0) && (
                  <Tab
                    label='Itinerary'
                    value='itinerary'
                  />
                )}
                {(canEdit || !isNotesEmpty(currentTrip!)) && (
                  <Tab
                    label='Notepad'
                    value='notepad'
                  />
                )}
              </TabList>

              <TabPanel
                className='trip-tab-panel'
                value='playground'
              >
                <TripPlayground
                  trip={currentTrip}
                  canEdit={canEdit}
                  handleMarkerClick={handleMarkerClick}
                />
              </TabPanel>
              <TabPanel
                className='trip-tab-panel'
                value='map'
              >
                <TripMap
                  center={mapCenter || location}
                  zoom={mapZoom}
                  markerOnClick={handleMarkerClick}
                  onCenterChanged={handleMapCenterChange}
                  onZoomChanged={handleMapZoomChange}
                  fitBounds={!mapCenter || !mapZoom}
                />
              </TabPanel>
              {(canEdit || Object.keys(currentTrip?.events || {}).length > 0) && (
                <TabPanel
                  className='trip-tab-panel'
                  value='itinerary'
                >
                  <Itinerary
                    trip={currentTrip}
                    pendingDrop={pendingDrop}
                    onConfirmDrop={handleConfirmDrop}
                    onCancelDrop={handleCancelDrop}
                  />
                </TabPanel>
              )}
              {(canEdit || !isNotesEmpty(currentTrip!)) && (
                <TabPanel
                  className='trip-tab-panel'
                  value='notepad'
                >
                  <div className={`trip-notes-container ${canEdit ? 'can-edit' : ''}`}>
                    <RichTextEditor
                      initialValue={notes}
                      onChange={(newNotes: Descendant[]) => {
                        debouncedSaveNotes(newNotes);
                      }}
                      toolbar={canEdit}
                      placeholder={
                        canEdit
                          ? 'A space to add misc. notes, like a packing list, or other trip details...'
                          : null
                      }
                      readOnly={!canEdit}
                    />
                  </div>
                </TabPanel>
              )}
            </TabContext>
          </Box>

          <DragOverlay dropAnimation={DROP_ANIMATION}>
            <DragOverlayContent activeItem={activeItem} />
          </DragOverlay>
        </DndContext>
      </div>

      {cardDetailToDisplayId &&
        tripId &&
        cards &&
        cards[cardDetailToDisplayId] &&
        currentTab === 'map' &&
        (isMobile ? (
          <SingleCard
            card={cards[cardDetailToDisplayId]}
            tripId={tripId}
            canEdit={canEdit}
            handleClose={handleCardDetailsClose}
          />
        ) : (
          <CardDetailsModal
            card={cards[cardDetailToDisplayId]}
            tripId={tripId}
            canEdit={canEdit}
            open={cardDetailToDisplayId !== null}
            handleClose={handleCardDetailsClose}
          />
        ))}
    </>
  );
}

Trip.displayName = 'TripPage';
export default Trip;
